import { useMemo } from 'react';
import type { PortfolioReport } from '../types';
import { enrichPortfolioReport } from '../utils/enrichPortfolioReport';
import {
  filterPortfolioReports,
  resolvePortfoliosListContext,
  type ViewMode,
} from '../utils/portfolioFilters';
import { sliceClientPage } from '../utils/portfolioTabPagination';

export function usePortfolioDisplayedRows({
  portfolios,
  debouncedSearch,
  modelFilter,
  isClientFilter,
  viewMode,
  page,
  pageSize,
  totalCount,
}: {
  portfolios: PortfolioReport[];
  debouncedSearch: string;
  modelFilter: string[];
  isClientFilter: boolean;
  viewMode: ViewMode;
  page: number;
  pageSize: number;
  totalCount: number;
}) {
  const { debouncedSearch: activeSearch } = resolvePortfoliosListContext(
    viewMode,
    {
      debouncedSearch,
      modelFilter,
    },
  );

  const filteredPortfolios = useMemo(() => {
    const reports = isClientFilter
      ? filterPortfolioReports({
          reports: portfolios,
          search: activeSearch,
        })
      : portfolios;
    return reports.map((report) =>
      enrichPortfolioReport(report as PortfolioReport),
    );
  }, [portfolios, activeSearch, isClientFilter]);

  const displayedPortfolios = useMemo(() => {
    if (isClientFilter) {
      return sliceClientPage(filteredPortfolios, page, pageSize);
    }
    return filteredPortfolios;
  }, [filteredPortfolios, isClientFilter, page, pageSize]);

  const totalPages = isClientFilter
    ? Math.max(1, Math.ceil(filteredPortfolios.length / pageSize))
    : Math.max(1, Math.ceil(totalCount / pageSize));

  return {
    filteredPortfolios,
    displayedPortfolios,
    totalPages,
  };
}
